<script setup lang="ts">
import { UiButton, UiEventComponent } from '@ui'
import type { EventEntry } from '~~/types/types'

const props = defineProps<{
  data: string
}>()

const {
  public: { appLink },
} = useRuntimeConfig()

const { t } = useI18n()
const route = useRoute()
const { width } = useWindowSize()
const localeRoute = useLocaleRoute()

const number = ref(10)
const isLoading = ref(false)
const relatedEventsData = ref<any[]>([])
const nowCursor = ref<string | null>(null)

const hubId = computed(() => props.data)
const isMobile = computed(() => width.value < 431.9)
const slug = computed(() => route.params.slug as string)

const queryData = computed(() => ({
  hubId: hubId.value,
  cursor: nowCursor.value || undefined,
  take: number.value,
}))

const { data: initialData } =
  await useNuxtApp().$client.hubs.getActivitiesByHubNew.useQuery({
    hubId: hubId.value,
    take: number.value,
  })

if (initialData.value) {
  relatedEventsData.value = initialData.value.activities || []
  nowCursor.value = initialData.value.nextCursor || null
}

const loadMore = async () => {
  isLoading.value = true

  try {
    const { activities, nextCursor } =
      await useNuxtApp().$client.hubs.getActivitiesByHubNew.query({
        ...queryData.value,
      })

    if (activities.length > 0) {
      relatedEventsData.value.push(...activities)
      nowCursor.value = nextCursor
    } else {
      nowCursor.value = null
    }
  } catch (error) {
    console.error('Error in loadMore:', error)
  }

  isLoading.value = false
}

function isUrlSimple(str: string): boolean {
  return str.startsWith('http://') || str.startsWith('https://')
}

function getEventLink(event: EventEntry): string {
  const chainAtlasLinks: Record<string, string> = {
    '6787b5304cdf8256b108edfc': 'https://bit.ly/3PDTi1X',
    '6787b88e4cdf8256b108ee09': 'https://bit.ly/4jd8P6x',
    '6787b97b4cdf8256b108ee0e': 'https://bit.ly/4hiPcYL',
  }

  const tonXdaoLinks: Record<string, string> = {
    '6787bbd84cdf8256b108ee16': 'https://bit.ly/40ni8rE',
    '678843d3e7b246a90e9ca0cd': 'https://bit.ly/42jvQPe',
    '67884296e7b246a90e9ca0c8': 'https://bit.ly/3Wolfyq',
  }

  const route = localeRoute('/events/' + event.id)

  if (slug.value === 'chainatlas-skale') {
    return chainAtlasLinks[event.id] || route?.fullPath || '/'
  } else if (slug.value === 'tonxdao') {
    return tonXdaoLinks[event.id] || route?.fullPath || '/'
  } else {
    return route?.fullPath || '/'
  }
}
</script>

<template>
  <div
    v-if="relatedEventsData.length"
    class="w-full flex flex-col items-center gap-5"
  >
    <slot name="title" />
    <ClientOnly>
      <div
        class="gap-5 justify-center px-5 max-w-252.5 mx-auto lg:px-0"
        :class="isMobile ? 'flex flex-col ' : 'flex flex-row flex-wrap'"
      >
        <NuxtLink
          v-for="(event, index) in relatedEventsData"
          :key="index"
          :to="getEventLink(event)"
          class="no-underline"
        >
          <UiEventComponent
            :cover-image="
              isUrlSimple(event.coverImage)
                ? slug === 'burner-map'
                  ? 'https://content.belong.net/p/content/header_events_1_ea77fb7722.jpeg'
                  : event.coverImage
                : `${appLink}/up/activities/${event.id}_${event.coverImage}.jpg`
            "
            :variant="isMobile ? 'with-block' : 'bottom-bar'"
            :is-site="true"
            :class="isMobile ? 'bg-[#292A32] rounded-2xl' : ''"
          >
            <template #location>
              <div v-if="event.online" class="flex flex-row gap-2 w-full">
                <Icon name="belong:event-online" class="min-w-4 w-4 h-4" />
                <p class="text-white text-xs font-medium truncate m-0">
                  Online
                </p>
              </div>

              <div v-else class="flex flex-row items-center gap-1 w-full">
                <Icon
                  name="belong:icon-location-violet"
                  class="min-w-2.5 w-2.5 h-2.5"
                />

                <p class="text-white text-xs font-medium truncate w-full">
                  {{ event.place.country.name }}
                </p>
              </div>
            </template>
            <template #title>
              {{ event.name }}
            </template>
            <template #description>
              {{ event.description }}
            </template>
            <template #date>
              <UiDateTime :value="event.start" format="MMM D YYYY" />
            </template>
            <template #time>
              <UiDateTime :value="event.start" format="LT" />
            </template>

            <template #mask>
              <Icon
                name="belong:mask-event"
                class="bg-[#151515] absolute top-0 bottom-0 left-0 right-0 z-1 w-12.5 h-2.25 mx-auto"
              />
            </template>
          </UiEventComponent>
        </NuxtLink>
      </div>

      <Transition name="fade">
        <div v-if="isLoading" class="w-20 mx-auto">
          <Lottie src="/lottie/loading.json" autoplay loop />
        </div>
      </Transition>

      <div v-if="nowCursor" class="px-3.5 max-w-100 w-full md:mx-0">
        <UiButton @click="loadMore">
          <p class="text-white text-center">
            {{ t('load-more') }}
          </p>
        </UiButton>
      </div>
    </ClientOnly>
  </div>
</template>

<style scoped>
@reference "tailwindcss";
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
